import { useState } from "react";
import type { CounterPrinted } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { counterToast } from "../printing/print-notices";
import { ReportReceipt } from "./ReportReceipt";
import type { ReportPrint } from "./reports";

interface Props {
  print: ReportPrint;
  /**
   * Prints it again on the printer of the Z-rapport (a Z, from the close or the archive); resolves
   * with where, or null when the shop has no printer: then the browser prints it. Without it (an X
   * that was just printed on the receipt printer) there is no button, as in the prototype.
   */
  onPrint?: (() => Promise<CounterPrinted>) | undefined;
  /** Not printed by the server (no printer): "Afdrukken" prints from the browser (80 mm). */
  browser?: boolean;
  onClose: () => void;
}

/**
 * The X or Z receipt on screen ("X-rapport (tussenstand)", "Z-bon 12", prototype openZBon). The X and
 * the Z are printed on the printer of the Z-rapport when they are made (blok I); a Z can be printed
 * again. A shop without that printer prints from the browser.
 */
export function ReportDialog({ print, onPrint, browser = false, onClose }: Props) {
  const { toast, report } = useTill();
  const [busy, setBusy] = useState(false);
  const text = t.reports.bon;

  const printAgain = async () => {
    if (!onPrint) {
      window.print();
      return;
    }
    setBusy(true);
    try {
      const said = counterToast(await onPrint());
      if (said === null) window.print();
      else toast(said);
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={print.kind === "x" ? text.xTitle : text.zTitle(print.number ?? 0)}
      onClose={onClose}
      footer={
        browser || onPrint ? (
          <>
            <span className="spacer" />
            <button type="button" className="btn" disabled={busy} onClick={() => void printAgain()}>
              {text.print}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="bonwrap zbonpanel">
        <ReportReceipt print={print} />
      </div>
    </Dialog>
  );
}
